import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import { formatObjective } from '../../utils/portfolioDesignResults';
import { runDistributions, runLinePath } from '../../utils/searchRunDistributions';

const top = 34;
const height = 164;

export default function SearchRunDistributions({ columns, trials, width, showLines, selectedIndex, hoveredIndex, onHover, onChoose }) {
    const theme = useTheme();
    const axes = useMemo(() => runDistributions(columns, trials), [columns, trials]);
    // Paint the selected portfolio last so hover previews cannot obscure it.
    const layer = (row) => row.index === selectedIndex ? 2 : row.index === hoveredIndex ? 1 : 0;
    const rows = trials.filter((row) => row.status !== 'running').sort((a, b) => layer(a) - layer(b));
    return <Box component="svg" aria-label="Run distributions" width={width} height={236} sx={{ display: 'block', ml: '1px', overflow: 'visible',
        '& .run-curve:focus-visible': { outline: 'none', strokeWidth: 4, opacity: 1 } }}>
        <title>Distributions by column; lines connect the values in each run.</title>
        {axes.map((axis) => <g key={axis.key} data-column={axis.key}>
            <text x={axis.x} y={17} textAnchor="middle" fill={theme.palette.text.secondary} fontSize={11}>{formatObjective(axis.max, { precision: Math.min(axis.precision, 2) }, true)}</text>
            <line x1={axis.x} x2={axis.x} y1={top} y2={top + height} stroke={theme.palette.divider} />
            {axis.bins.map((count, i) => count > 0 && <rect key={i} x={axis.x} y={top + height - (i + 1) * height / 8}
                width={count / Math.max(...axis.bins) * (axis.width / 2 - 16)} height={height / 8 - 1}
                fill={theme.palette.primary.main} opacity={0.32}>
                <title>{axis.label}: {count} {count === 1 ? 'run' : 'runs'} in this interval</title>
            </rect>)}
            <text x={axis.x} y={top + height + 20} textAnchor="middle" fill={theme.palette.text.secondary} fontSize={11}>{formatObjective(axis.min, { precision: Math.min(axis.precision, 2) }, true)}</text>
        </g>)}
        {showLines && rows.map((row) => <path key={row.index} className="run-curve" role="button" tabIndex={0}
            aria-label={`Inspect run ${row.number} on chart`} aria-pressed={row.index === selectedIndex}
            d={runLinePath(axes, row, top, height)} fill="none"
            stroke={row.index === selectedIndex ? theme.palette.error.main : row.index === hoveredIndex ? theme.palette.primary.main : theme.palette.text.secondary}
            strokeWidth={row.index === selectedIndex ? 3.5 : row.index === hoveredIndex ? 2.5 : 1}
            opacity={row.index === selectedIndex || row.index === hoveredIndex ? 1 : 0.15}
            strokeDasharray={row.status === 'verified' ? undefined : '4 4'} style={{ cursor: 'pointer' }}
            onMouseEnter={() => onHover(row.index)} onMouseLeave={() => onHover(null)}
            onFocus={() => onHover(row.index)} onBlur={() => onHover(null)} onClick={() => onChoose(row.index)}
            onKeyDown={(event) => { if (['Enter', ' '].includes(event.key)) { event.preventDefault(); onChoose(row.index); } }}>
            <title>Run {row.number}</title>
        </path>)}
    </Box>;
}
